import Image from "next/image";
import { toCardRecord, type CardRecord } from "@/lib/posts-helpers";
import type { PostRecord, HashtagRecord } from "@/types/api";
import { StickerCard } from "@/components/stickers/StickerCard";
import { inGridAdNode, shouldInsertInGridAdAfter } from "@/components/ads/inGridAdCadence";
import { SITE } from "@/lib/seo/site";

interface StickerGridProps {
  records: Array<CardRecord | PostRecord | HashtagRecord>;
  withInGridAds?: boolean;
  startIndex?: number;
  /**
   * Heading level for each card title. Defaults to "h3", which is right for
   * pages that render an h2 above the grid. Pages whose intro is a `<p>` —
   * `HashtagRoute` — pass "h2" so the outline has no skipped level. See
   * `StickerCardProps.titleAs`.
   */
  cardTitleAs?: "h2" | "h3";
}

export function StickerGrid({
  records: incoming,
  withInGridAds = false,
  startIndex = 0,
  cardTitleAs,
}: StickerGridProps) {
  // Project here rather than trusting callers. A full PostRecord is structurally
  // assignable to CardRecord, so the narrower prop type alone would not have
  // stopped a caller handing over all 37 fields — and on the server-rendered
  // grids (HashtagRoute, /search, TrendingFeed) StickerCard forwards the record
  // straight into the client-only StickerCardDesktopMeta/Blurb, which serializes
  // whatever object it is given. Doing it once here covers every path.
  const records: CardRecord[] = incoming.map(toCardRecord);

  if (records.length === 0) {
    return (
      <div className="rounded-card flex flex-col items-center gap-4 bg-white px-6 py-10 text-center shadow-(--shadow-card)">
        <Image
          src={`${SITE.cdn}/static-content/nostickerpackfound.png`}
          alt="No sticker pack found"
          width={180}
          height={180}
          className="opacity-90"
        />
        <p className="text-foreground text-[15px] font-medium">No sticker pack found</p>
        <p className="text-muted-foreground text-[13px]">
          Check back soon — publishers add new packs throughout the year.
        </p>
      </div>
    );
  }

  const items: React.ReactNode[] = [];
  let adsInserted = 0;
  records.forEach((record, idx) => {
    const absoluteIndex = startIndex + idx;
    items.push(
      <StickerCard
        key={`${record.post_Id}-${absoluteIndex}`}
        record={record}
        index={absoluteIndex}
        titleAs={cardTitleAs}
      />,
    );
    if (withInGridAds && shouldInsertInGridAdAfter(idx, adsInserted)) {
      items.push(inGridAdNode(`ad-${absoluteIndex}`));
      adsInserted += 1;
    }
  });

  return (
    <div data-track-view="sticker_grid" className="flex flex-col gap-6">
      {items}
    </div>
  );
}
